import axios from "axios";

import { resolveBackendUrl } from "./backend-url";

export const BACKEND_URL = resolveBackendUrl(process.env.REACT_APP_BACKEND_URL);
const API_BASE_URL = `${BACKEND_URL}/api`;
const CSRF_COOKIE_NAME = process.env.REACT_APP_CSRF_COOKIE_NAME || "csrf_token";
const CSRF_HEADER_NAME = process.env.REACT_APP_CSRF_HEADER_NAME || "X-CSRF-Token";
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);
const AUTH_ENDPOINTS_WITHOUT_RETRY = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/forgot-password",
  "/auth/reset-password",
];
let refreshPromise = null;
let authOperationGeneration = 0;

export function beginAuthOperation() {
  authOperationGeneration += 1;
  return authOperationGeneration;
}

function getCookieValue(name) {
  if (typeof document === "undefined") {
    return null;
  }

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  return decodeURIComponent(cookie.split("=")[1] || "");
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  config._polenicaAuthGeneration = authOperationGeneration;
  const method = String(config.method || "get").toLowerCase();
  if (!UNSAFE_METHODS.has(method)) {
    return config;
  }

  const csrfToken = getCookieValue(CSRF_COOKIE_NAME);
  if (csrfToken) {
    config.headers = {
      ...config.headers,
      [CSRF_HEADER_NAME]: csrfToken,
    };
  }

  return config;
});

function isAuthenticationEndpoint(url = "") {
  return AUTH_ENDPOINTS_WITHOUT_RETRY.some((endpoint) => String(url).includes(endpoint));
}

function isCsrfFailure(error) {
  const detail = error.response?.data?.detail;
  return error.response?.status === 403 && typeof detail === "string" && detail.toLowerCase().includes("csrf");
}

async function renewSession() {
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post("/auth/refresh")
      .then((response) => {
        window.dispatchEvent(new CustomEvent("polenica:session-refreshed", { detail: response.data?.user }));
        return response;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const canRenew =
      originalRequest &&
      !originalRequest._polenicaRetried &&
      !isAuthenticationEndpoint(originalRequest.url) &&
      (error.response?.status === 401 || isCsrfFailure(error));

    if (canRenew) {
      originalRequest._polenicaRetried = true;
      try {
        await renewSession();
        return api(originalRequest);
      } catch (refreshError) {
        refreshError.isSessionExpired = true;
        if (originalRequest._polenicaAuthGeneration === authOperationGeneration) {
          window.dispatchEvent(new CustomEvent("polenica:session-expired"));
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export function getApiErrorMessage(error, fallback = "No fue posible completar la operación.") {
  if (error?.code === "ECONNABORTED") {
    return "La operación tardó demasiado. Verifique la conexión e inténtelo nuevamente.";
  }
  if (error?.isSessionExpired || error?.response?.status === 401 || isCsrfFailure(error)) {
    return "La sesión necesita renovarse. Inicie sesión nuevamente; el borrador permanecerá guardado.";
  }

  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || "Dato inválido").join(" ");
  }
  return fallback;
}
